/**
 * `parsePrep` — markdown → six-section structure + question parser (Batch 30j-4).
 *
 * Pure function. Port of prototype `parsePrepSections` + `parseQuestions`
 * (`~/certain-prototype/public/pipeline.html:1359-1396`).
 *
 * Spec: `frontend/docs/30j_4_design.md` §3.3 + §3.5 +
 *       `frontend/docs/30j_4_architect.md` §7.
 *
 * The shipped backend prompt at `backend/app/ai/prompts.py:61-85` emits
 * exactly six `## H2` sections (in source order — Background / Strengths /
 * Probe / Questions / RedFlags / Strategy). Display order is independent
 * of source order; the parser maps headings to canonical keys via
 * substring match (case-insensitive). The display order is owned by
 * `<PrepSplitPanel>`'s `PREP_NAV_ITEMS`.
 *
 * Question parser: each Q renders as `**Q[N]: <text>**\nFollow-up: <text>`.
 * Block-split on `\n\n(?=\*\*Q)` separates questions; numbering is
 * 1-indexed by parse order so a missing `**Q3:` doesn't gap the display.
 *
 * Substring-match limitation (documented edge case row 6): if a future
 * prompt adds a section like `## Predicted Outcomes`, the parser's
 * `key.includes("red")` does NOT match (no "red" substring in
 * "predicted") — but a heading like `## Strengths Reduction` would
 * match BOTH `strength` AND `red`; the order-of-checks (`strength`
 * first) wins. Engineer notes: the current six headings have no
 * collisions; row 6 in the test table flags this if regression occurs.
 *
 * Pure: no React, no hooks. Caller memoizes (`useMemo` in
 * `<InterviewPrepTab>` keyed on `row.content`).
 */

/** Six canonical prep section keys. Consumed by `<PrepSplitPanel>` (commit 4). */
export type PrepSectionKey =
  | "background"
  | "strengths"
  | "probe"
  | "redFlags"
  | "strategy"
  | "questions";

export interface PrepQuestion {
  /** 1-indexed question number, assigned by parse order. */
  number: number;
  /** The question text (newlines collapsed to spaces). */
  question: string;
  /** Follow-up rationale, possibly markdown. Null if absent. */
  followUp: string | null;
}

export interface ParsedPrep {
  /**
   * Section bodies keyed by canonical key. All six keys present
   * (empty string if the parser didn't find that section).
   */
  sections: Record<PrepSectionKey, string>;
  /** Parsed questions. Empty array if no Questions section or none parsed. */
  questions: PrepQuestion[];
}

/** Empty result with all six section keys present (empty bodies). */
function emptyResult(): ParsedPrep {
  return {
    sections: {
      background: "",
      strengths: "",
      probe: "",
      redFlags: "",
      strategy: "",
      questions: "",
    },
    questions: [],
  };
}

/**
 * Parse the Questions section body into structured `PrepQuestion[]`.
 * Block-split on `\n\n(?=\*\*Q)` matches the prototype's heuristic.
 * Numbering is 1-indexed by parse order (NOT extracted from "Q1" in
 * the source) so a prompt response missing `**Q3:` doesn't create a
 * numbering gap in display.
 */
export function parseQuestions(body: string): PrepQuestion[] {
  if (!body) return [];
  const qs: PrepQuestion[] = [];
  const blocks = body.split(/\n\n(?=\*\*Q)/);
  let n = 1;
  for (const block of blocks) {
    // Match `**Q:`, `**Q1:`, `**Q1`, etc. The `\d*` handles bare
    // `**Q:` and numbered `**Q1:` / `**Q12:` alike. The `:?` allows
    // unnumbered Qs without a colon.
    const qm = block.match(/\*\*Q\d*:?\s*([\s\S]+?)\*\*/);
    if (!qm) continue;
    const question = (qm[1] ?? "").replace(/\n/g, " ").trim();
    // Remainder of the block, after the bolded Q.
    const after = block.slice(block.indexOf(qm[0]) + qm[0].length).trim();
    // Strip leading "Follow-up:" prefix (case-insensitive). Empty
    // string after strip → null.
    const stripped = after.replace(/^Follow-up:\s*/i, "").trim();
    const followUp = stripped.length > 0 ? stripped : null;
    qs.push({ number: n, question, followUp });
    n += 1;
  }
  return qs;
}

/**
 * Parse a markdown prep guide into structured six-section + questions
 * form.
 *
 * Flow:
 *   1. Defensive: empty / whitespace input → empty result.
 *   2. Split on `## H2` headings.
 *   3. For each section, substring-match the heading (lowercased) against
 *      one of the six canonical keys.
 *   4. Run `parseQuestions` on the Questions section body.
 *
 * Substring match precedence (first match wins):
 *   `strength` → strengths
 *   `probe`    → probe
 *   `question` → questions
 *   `red`      → redFlags
 *   `strategy` → strategy
 *   `background` → background
 */
export function parsePrep(md: string): ParsedPrep {
  if (typeof md !== "string" || md.trim().length === 0) {
    return emptyResult();
  }

  const sections: Record<PrepSectionKey, string> = {
    background: "",
    strengths: "",
    probe: "",
    redFlags: "",
    strategy: "",
    questions: "",
  };

  const parts = md.split(/\n(?=## )/);
  for (const part of parts) {
    const m = part.match(/^## (.+?)\n([\s\S]*)/);
    if (!m) continue;
    const title = (m[1] ?? "").trim();
    const body = (m[2] ?? "").trim();
    const key = title.toLowerCase();

    // First-match-wins. Order matters: `strength` before `red` so a
    // hypothetical "Strengths Reduction" heading binds to strengths,
    // not redFlags. (Current prompt has no such collision; this is
    // defensive against prompt drift.)
    if (key.includes("strength")) sections.strengths = body;
    else if (key.includes("probe")) sections.probe = body;
    else if (key.includes("question")) sections.questions = body;
    else if (key.includes("red")) sections.redFlags = body;
    else if (key.includes("strategy")) sections.strategy = body;
    else if (key.includes("background")) sections.background = body;
    // Headings outside the six canonical keys are silently ignored.
    // (e.g., a future `## Scorecard` block from prompt drift would not
    // land in any section; engineer can add a console.warn in dev mode
    // if surprised.)
  }

  const questions = parseQuestions(sections.questions);

  return { sections, questions };
}
